import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { Page } from '@app/app/shared/models/page';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';
import {
  AdminCoursesRequest,
  CohortGradeData,
  CohortOption,
  ProgramCourseItem,
  ProgramCoursesAnalyticsFilters,
  ProgramCoursesOverview,
  StudentEngagementData,
} from '../../models/courses-analytics.model';
import { CoursesAnalyticsDataService } from './courses-analytics-data.service';

@Injectable()
export class ApiCoursesAnalyticsDataService implements CoursesAnalyticsDataService {
  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getAdminCourses(requestParams?: AdminCoursesRequest): ObservableResult<Page<ProgramItem>> {
    const params: Record<string, string> = {};
    if (requestParams?.programmeId) {
      params['inProgram'] = requestParams.programmeId;
    }
    if (requestParams?.orgClientId) {
      params['orgClientId'] = requestParams.orgClientId;
    }
    if (requestParams?.page != null) {
      params['page'] = requestParams.page.toString();
    }
    if (requestParams?.size != null) {
      params['size'] = requestParams.size.toString();
    }
    if (requestParams?.term) {
      params['term'] = requestParams.term;
    }
    if (requestParams?.sortBy) {
      params['sortBy'] = requestParams.sortBy;
    }

    return this.client.get<Page<ProgramItem>>(Location.joinWithSlash(environment.apiRootUrl || '', 'analytics/courses/admin'), params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCourses'))),
    );
  }

  getOverview(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCoursesOverview> {
    const params = this.buildPlaylistParams(filters);
    return this.client
      .get<ProgramCoursesOverview>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/overview`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsOverview'))),
      );
  }

  getCourses(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<ProgramCourseItem[]> {
    const params: Record<string, string> = {};
    if (filters?.searchQuery) {
      params['search'] = filters.searchQuery;
    }
    if (filters?.statusFilter && filters.statusFilter !== 'all') {
      params['status'] = filters.statusFilter === 'active' ? 'Active' : 'Ended';
    }
    if (filters?.cohort) {
      params['cohort'] = filters.cohort;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }

    return this.client
      .get<ProgramCourseItem[]>(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCourses'))),
      );
  }

  getCohortGradeComparison(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<CohortGradeData[]> {
    const params: Record<string, string> = {};
    if (filters?.cohortNames?.length) {
      params['cohortNames'] = filters.cohortNames.join(',');
    }
    if (filters?.metric) {
      params['metric'] = filters.metric.toUpperCase();
    }
    if (filters?.dateRange?.start) {
      params['startDate'] = filters.dateRange.start.toISOString();
    }
    if (filters?.dateRange?.end) {
      params['endDate'] = filters.dateRange.end.toISOString();
    }

    return this.client
      .get<
        CohortGradeData[]
      >(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/cohort-comparison`), params)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() =>
          ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohortComparison')),
        ),
      );
  }

  getStudentEngagement(programId: string, filters?: ProgramCoursesAnalyticsFilters): ObservableResult<StudentEngagementData> {
    const params = this.buildPlaylistParams(filters);

    return this.client
      .get<StudentEngagementData>(
        Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/courses/engagement`),
        params,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsEngagement'))),
      );
  }

  getCohorts(programId: string): ObservableResult<CohortOption[]> {
    return this.client
      .get<CohortOption[]>(Location.joinWithSlash(environment.apiRootUrl || '', `analytics/programs/${programId}/cohorts`))
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetAnalyticsCohorts'))),
      );
  }
  private buildPlaylistParams(filters?: ProgramCoursesAnalyticsFilters): Record<string, string> {
    const params: Record<string, string> = {};
    if (filters?.includedPlaylistIds?.length) {
      params['playlistIds'] = filters.includedPlaylistIds.join(',');
    }
    if (filters?.cohort) {
      params['cohort'] = filters.cohort;
    }
    if (filters?.company) {
      params['company'] = filters.company;
    }
    return params;
  }
}
